import { useEffect, useState } from 'react';
import type { App } from '@hangar/shared';
import { toast } from 'sonner';
import { ApiError, problemContext } from '@/api/client';
import type { BitriseProvisionInput } from '@/api/endpoints';
import { scmUnsupported, useBitriseSetup, useBitriseSetupMutations, useProviderAccounts, useScmConnections, useScmRepositories } from '@/api/hooks';
import type { BitriseSetup, ExistingBitriseApp } from '@/api/schemas';
import { useCan } from '@/lib/permissions';
import {
  SETUP_FAILED_CODE,
  bitriseAccountsOf,
  defaultChoice,
  isDone,
  parseExistingApps,
  pickById,
  provisionInput,
  provisionedToast,
  retryInput,
  usableConnections,
  withBitriseApp,
} from './bitriseModel';

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** The repositories of a workspace: a debounced name filter and the repository picked from the list. */
function useRepositoryPicker(connectionId: string | undefined) {
  const [filter, setFilter] = useState('');
  const query = useDebounced(filter.trim(), 300);
  const repos = useScmRepositories(connectionId, query);
  const [repository, setRepository] = useState('');
  return { filter, setFilter, repos, repository, setRepository };
}

/** What Hangar can create the Bitrise app from: the Bitbucket workspace, the Bitrise account and the repository picked. */
function useBitriseSources() {
  const connections = useScmConnections();
  const accounts = useProviderAccounts();
  const [connectionId, setConnectionId] = useState<string | undefined>();
  const [accountId, setAccountId] = useState<string | undefined>();
  const usable = usableConnections(connections.data);
  const bitriseAccounts = bitriseAccountsOf(accounts.data);
  const connection = pickById(usable, connectionId);
  const picker = useRepositoryPicker(connection?.id);
  const selectConnection = (id: string) => {
    setConnectionId(id);
    picker.setRepository('');
  };
  return { connections, accounts, usable, bitriseAccounts, connection, account: pickById(bitriseAccounts, accountId), selectConnection, selectAccount: setAccountId, ...picker };
}

/** A 409 "Bitrise already has an app for this repository": the apps it lists, else null. */
function existingAppsOf(err: unknown): ExistingBitriseApp[] | null {
  const ctx = problemContext(err);
  if (!(err instanceof ApiError && err.status === 409 && ctx.reason === 'bitrise_app_exists' && Array.isArray(ctx.existingApps))) return null;
  return parseExistingApps(ctx.existingApps);
}

/** The apps Bitrise already has for the repository, and which one the person chose (or "create another"). */
function useExistingChoice() {
  const [existing, setExisting] = useState<ExistingBitriseApp[] | null>(null);
  const [choice, setChoice] = useState<string>('create');
  const offer = (apps: ExistingBitriseApp[]) => {
    setExisting(apps);
    setChoice(defaultChoice(apps));
  };
  return { existing, choice, setChoice, offer, cancel: () => setExisting(null) };
}

/** A failed step is shown on the step itself, with how to fix it: no second copy of it in an alert. */
const isSetupFailure = (err: unknown): boolean => err instanceof ApiError && err.code === SETUP_FAILED_CODE;

function notifyProvisioned(appName: string, result: BitriseSetup): void {
  if (result.status !== 'done') return;
  const t = provisionedToast(appName, result);
  toast.success(t.title, { description: t.description });
}

/** The provision and sync requests, the existing-app choice and the problem a failure leaves. */
function useProvision(app: App) {
  const { provision, sync } = useBitriseSetupMutations(app.id);
  const choose = useExistingChoice();
  const [problem, setProblem] = useState<unknown>(null);

  const run = async (body: BitriseProvisionInput) => {
    setProblem(null);
    choose.cancel();
    try {
      notifyProvisioned(app.name, await provision.mutateAsync(body));
    } catch (err) {
      const apps = existingAppsOf(err);
      if (apps) choose.offer(apps);
      else if (!isSetupFailure(err)) setProblem(err);
    }
  };
  const doSync = async () => {
    setProblem(null);
    try {
      await sync.mutateAsync();
      toast.success('Bitrise workflows synced', { description: 'The workflows on Bitrise now match the profiles.' });
    } catch (err) {
      setProblem(err);
    }
  };
  return { run, doSync, existing: choose.existing, cancelExisting: choose.cancel, choice: choose.choice, setChoice: choose.setChoice, problem, running: provision.isPending || sync.isPending, provisioning: provision.isPending, syncing: sync.isPending };
}

/** Live progress: the request is one call, the recorded steps are read while it runs. */
function useBitriseLive(appId: string, running: boolean) {
  const live = useBitriseSetup(appId, running);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => void live.refetch(), 1500);
    return () => clearInterval(t);
  }, [running, live]);
  return live;
}

/** Everything the "Create on Bitrise" panel shows and does: the setup, what is picked, and the start / retry / sync actions. */
export function useBitrisePanel(app: App) {
  const admin = useCan('admin');
  const setup = useBitriseSetup(app.id);
  const src = useBitriseSources();
  const prov = useProvision(app);
  const live = useBitriseLive(app.id, prov.running);
  const data: BitriseSetup | undefined = (prov.running ? live.data : undefined) ?? setup.data;
  const base = () => provisionInput(src.connection, src.repository, src.account);

  const start = () => {
    const body = base();
    if (body) void prov.run(body);
  };
  const confirmExisting = () => {
    const body = base();
    if (body) void prov.run(withBitriseApp(body, prov.choice));
  };
  const retry = () => {
    const body = retryInput(data);
    if (body) void prov.run(body);
    else start();
  };
  // Not served on this API: the panel renders nothing.
  const hidden = setup.isError && scmUnsupported(setup.error);
  return { admin, data, done: isDone(data), hidden, src, prov, start, confirmExisting, retry };
}

export type BitrisePanelState = ReturnType<typeof useBitrisePanel>;
